import { api, unwrap } from "../lib/axios";
import type { ApiEnvelope } from "../types/api";

export interface UploadResult {
  key: string;
  bucket: string;
  location: string; // direct S3 URL (private bucket — render via presignedGet)
  size: number;
  mimeType: string;
}

export type UploadType = "image" | "document" | "general" | "chat";

export const uploadsApi = {
  /**
   * Upload a file to S3 via the backend (multipart, field name `file`).
   * Returns the stored `location` (S3 URL) + `key`. The bucket is private, so
   * render the result through `presignedGet(key)`.
   */
  async uploadFile(
    file: File,
    opts: { uploadType?: UploadType; folder?: string } = {},
  ): Promise<UploadResult> {
    const form = new FormData();
    form.append("file", file);
    form.append("uploadType", opts.uploadType ?? "image");
    if (opts.folder) form.append("folder", opts.folder);

    const res = await api.post<ApiEnvelope<UploadResult>>(
      "/upload/file",
      form,
      // Let the browser set multipart/form-data + boundary (override the JSON default).
      { headers: { "Content-Type": undefined } },
    );
    return unwrap(res);
  },

  /** Get a short-lived presigned GET URL for a stored S3 key. */
  async presignedGet(key: string): Promise<string> {
    const res = await api.post<ApiEnvelope<{ downloadUrl: string }>>(
      "/upload/presigned-download-url",
      { key },
    );
    return unwrap(res).downloadUrl;
  },
};

/**
 * Extract the S3 key from a stored value. Stored media is usually a full
 * `https://<bucket>.s3.amazonaws.com/<key>` URL; we need just the key to presign.
 * Returns null for empty values; passes through values that already look like keys.
 */
export function s3KeyFromStored(value: string | undefined | null): string | null {
  if (!value) return null;
  const v = value.trim();
  if (!v) return null;
  if (!/^https?:\/\//i.test(v)) return v.replace(/^\/+/, ""); // already a key
  try {
    const u = new URL(v);
    return decodeURIComponent(u.pathname.replace(/^\/+/, ""));
  } catch {
    return v;
  }
}
